var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Checkbox, IconButton } from '@folio/stripes/components';
import { useTslEditor } from '../../search/tsl/react/useTslEditor';
import { highlightRuns } from '../../search/fieldQuery/highlight';
import css from './FieldQueryLine.css';
var LISTBOX_ID = 'czna-field-query-suggestions';
var optionId = function (index) { return "".concat(LISTBOX_ID, "-").concat(index); };
/**
 * CZNA-217 — the back-office query line: conditions on MARC fields and subfields
 * (`150a obsahuje matematik a ne 550 odpovídá věd`), with suggestions for fields,
 * relations and stored values, errors marked where they are in the text, and a
 * transcript saying in words what will be searched.
 *
 * <p>The transcript is built from what is SENT (the compiled tree), not from the typed
 * text, so it cannot describe a search other than the one that runs.
 */
var FieldQueryLine = function (_a) {
    var language = _a.language, value = _a.value, onChange = _a.onChange, matchCase = _a.matchCase, onMatchCaseChange = _a.onMatchCaseChange, onSubmit = _a.onSubmit, onClose = _a.onClose;
    var intl = useIntl();
    var _b = useState(false), triedWithErrors = _b[0], setTriedWithErrors = _b[1];
    // CZNA-1443 — how to write a query is there on request; the suggestions teach while typing.
    var _c = useState(false), helpOpen = _c[0], setHelpOpen = _c[1];
    var backdropRef = useRef(null);
    var inputRef = useRef(null);
    // CZNA-1443 — opened on purpose (a button in the search pane), so typing starts here.
    useEffect(function () {
        var _a;
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    var compiled = useMemo(function () { return language.compileText(value, matchCase); }, [language, value, matchCase]);
    var transcript = useMemo(function () { return language.describe(compiled); }, [language, compiled]);
    var submit = function () {
        if (compiled.hasErrors) {
            setTriedWithErrors(true);
            return;
        }
        setTriedWithErrors(false);
        onSubmit(value, compiled.words);
    };
    var editor = useTslEditor({
        registry: language.registry,
        value: value,
        onChange: function (next) {
            setTriedWithErrors(false);
            onChange(next);
        },
        onSubmit: submit,
        fetchValues: language.fetchValues,
        transformSuggestions: language.transformSuggestions,
    });
    var diagnostics = compiled.diagnostics;
    var runs = useMemo(function () { return highlightRuns(value, language.registry, compiled.diagnostics.filter(function (diagnostic) { return diagnostic.severity === 'error'; }).map(function (diagnostic) { return diagnostic.span; })); }, [value, language, compiled]);
    // CZNA-1443 — under the line stays the sentence, plus the match-case note (CZNA-1400:
    // it says the box is on); the general notes on diacritics move into the help.
    var matchCaseNote = matchCase
        ? intl.formatMessage({ id: 'ui-czna-authorities.fieldQuery.transcript.note.matchCase' })
        : undefined;
    var sentence = transcript[0], notes = transcript.slice(1);
    var helpNotes = notes.filter(function (note) { return note !== matchCaseNote; });
    return (React.createElement("section", { id: "czna-field-query-editor", className: css.fieldQuery, "aria-labelledby": "czna-field-query-label", "data-testid": "field-query-line" },
        React.createElement("div", { className: css.header },
            React.createElement("h3", { className: css.label, id: "czna-field-query-label" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.fieldQuery.label" })),
            React.createElement(Button, { buttonStyle: "link", marginBottom0: true, className: css.helpToggle, "aria-expanded": helpOpen, "aria-controls": "czna-field-query-guide", onClick: function () { return setHelpOpen(function (open) { return !open; }); }, "data-testid": "field-query-help-toggle" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.fieldQuery.howTo" })),
            onClose && (React.createElement(IconButton, { icon: "times", onClick: onClose, "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.fieldQuery.close' }), "data-testid": "field-query-close" }))),
        React.createElement("div", { className: css.row },
            React.createElement("div", { className: css.editor },
                React.createElement("div", { className: css.backdrop, ref: backdropRef, "aria-hidden": "true" },
                    runs.map(function (run, index) {
                        var className = [run.role && css[run.role], run.error && css.error].filter(Boolean).join(' ') || undefined;
                        return run.error
                            // eslint-disable-next-line react/no-array-index-key
                            ? React.createElement("mark", { key: index, className: className }, run.text)
                            // eslint-disable-next-line react/no-array-index-key
                            : React.createElement("span", { key: index, className: className }, run.text);
                    }),
                    ' '),
                React.createElement("textarea", __assign({ id: "czna-field-query-input", className: css.input, rows: 2, spellCheck: false, autoComplete: "off", role: "combobox", "aria-labelledby": "czna-field-query-label", "aria-autocomplete": "list", "aria-expanded": editor.isOpen, "aria-controls": LISTBOX_ID, "aria-activedescendant": editor.isOpen ? optionId(editor.activeIndex) : undefined, "aria-invalid": compiled.hasErrors, "aria-describedby": helpOpen ? 'czna-field-query-transcript czna-field-query-guide' : 'czna-field-query-transcript', placeholder: intl.formatMessage({ id: 'ui-czna-authorities.fieldQuery.placeholder' }), ref: function (element) {
                        inputRef.current = element;
                        editor.setInputElement(element);
                    } }, editor.inputProps, { onScroll: function (event) {
                        if (backdropRef.current) {
                            backdropRef.current.scrollTop = event.currentTarget.scrollTop;
                        }
                    }, "data-testid": "field-query-input" })),
                editor.isOpen && (React.createElement("ul", { id: LISTBOX_ID, className: css.suggestions, role: "listbox", "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.fieldQuery.suggestions' }), "data-testid": "field-query-suggestions" }, editor.suggestions.map(function (suggestion, index) { return (React.createElement("li", { 
                    // eslint-disable-next-line react/no-array-index-key
                    key: "".concat(suggestion.kind, "-").concat(suggestion.insertText, "-").concat(index), id: optionId(index), role: "option", "aria-selected": index === editor.activeIndex, className: index === editor.activeIndex ? css.activeSuggestion : css.suggestion, 
                    // mousedown, not click: the textarea's blur would close the list first
                    onMouseDown: function (event) {
                        event.preventDefault();
                        editor.applySuggestion(index);
                    } },
                    React.createElement("span", null, suggestion.label),
                    suggestion.detail && React.createElement("span", { className: css.detail }, suggestion.detail))); })))),
            React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, onClick: submit, "data-testid": "field-query-run" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.fieldQuery.run" })),
            value !== '' && (React.createElement(Button, { marginBottom0: true, onClick: function () {
                    onChange('');
                    setTriedWithErrors(false);
                }, "data-testid": "field-query-clear" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.fieldQuery.clear" })))),
        React.createElement(Checkbox, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.fieldQuery.matchCase" }), checked: matchCase, onChange: function (event) { return onMatchCaseChange(event.target.checked); }, "data-testid": "field-query-match-case" }),
        editor.isLoadingValues && (React.createElement("p", { className: css.hint },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.fieldQuery.loadingValues" }))),
        diagnostics.length > 0 && (React.createElement("ul", { className: css.diagnostics, "data-testid": "field-query-diagnostics" }, diagnostics.map(function (diagnostic, index) { return (React.createElement("li", { 
            // eslint-disable-next-line react/no-array-index-key
            key: index, className: diagnostic.severity === 'error' ? css.diagnosticError : css.diagnosticWarning },
            value.slice(diagnostic.span.start, diagnostic.span.end).trim() && (React.createElement("q", { className: css.fragment }, value.slice(diagnostic.span.start, diagnostic.span.end).trim())),
            language.formatDiagnostic(diagnostic))); }))),
        triedWithErrors && (React.createElement("p", { role: "alert", className: css.diagnosticError, "data-testid": "field-query-has-errors" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.fieldQuery.hasErrors" }))),
        React.createElement("div", { id: "czna-field-query-transcript", className: css.transcript, "data-testid": "field-query-transcript", "aria-live": "polite" }, sentence && (React.createElement(React.Fragment, null,
            React.createElement("span", { className: css.srOnly },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.fieldQuery.transcriptLabel" })),
            React.createElement("p", { className: css.sentence }, sentence),
            matchCaseNote && notes.includes(matchCaseNote) && React.createElement("p", { className: css.note }, matchCaseNote)))),
        helpOpen && (React.createElement("div", { id: "czna-field-query-guide", className: css.help, "data-testid": "field-query-help" },
            helpNotes.map(function (note) { return React.createElement("p", { key: note, className: css.note }, note); }),
            React.createElement("p", { className: css.hint },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.fieldQuery.guide" }))))));
};
export default FieldQueryLine;
